import React from 'react';
import styles from '../styles';
import useMediaQuery from '../hooks/useMediaQuery';
import { useLanguage } from '../contexts/LanguageContext';

import joergPhoto from '../assets/joerg-haupt.jpeg';
import prasadPhoto from '../assets/prasad-pawar.jpeg';

// ─── Founder Card ────────────────────────────────────────────────────────────
const FounderCard = ({ photo, name, role, bio }) => {
    const initials = name.split(' ').map(n => n[0]).join('');

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1,
            padding: '0 1.5rem',
        }}>
            {/* Circular photo */}
            <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                overflow: 'hidden',
                marginBottom: '1rem',
                border: '3px solid rgba(255,255,255,0.18)',
                background: 'rgba(255,255,255,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                {photo ? (
                    <img
                        src={photo}
                        alt={name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <span style={{
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '2rem',
                        fontWeight: '700',
                        fontFamily: "'Montserrat', sans-serif",
                    }}>
                        {initials}
                    </span>
                )}
            </div>

            {/* Name */}
            <p style={{
                color: '#FFFFFF',
                fontWeight: '700',
                fontSize: '1.05rem',
                marginBottom: '0.25rem',
                fontFamily: "'Montserrat', sans-serif",
                textAlign: 'center',
            }}>
                {name}
            </p>

            {/* Role — soft rose, readable on dark */}
            <p style={{
                color: '#FCA5A5',
                fontSize: '0.72rem',
                fontWeight: '700',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '0.9rem',
                textAlign: 'center',
            }}>
                {role}
            </p>

            {/* Bio */}
            <p style={{
                color: '#9CA3AF',
                fontSize: '0.875rem',
                lineHeight: '1.75',
                textAlign: 'center',
                maxWidth: '280px',
            }}>
                {bio}
            </p>
        </div>
    );
};

// ─── Footer ──────────────────────────────────────────────────────────────────
const Footer = () => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isTablet = useMediaQuery('(max-width: 640px)');
    const { t } = useLanguage();

    return (
        <footer style={{
            ...styles.section,
            backgroundColor: '#1F2937',
            color: '#FFFFFF',
            padding: isMobile ? '3rem 1.25rem 2rem' : '5rem 2rem 2.5rem',
            textAlign: 'center',
        }}>
            <div style={{
                maxWidth: '800px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}>

                {/* ── Section title ── */}
                <p style={{
                    fontSize: '0.7rem',
                    fontWeight: '800',
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: '#6B7280',
                    marginBottom: '2rem',
                }}>
                    {t('footer.headline')}
                </p>

                {/* ── Founder cards row ── */}
                <div style={{
                    display: 'flex',
                    flexDirection: isTablet ? 'column' : 'row',
                    alignItems: isTablet ? 'center' : 'flex-start',
                    justifyContent: 'center',
                    width: '100%',
                    gap: isTablet ? '2.5rem' : '0',
                    marginBottom: '3rem',
                }}>
                    <FounderCard
                        photo={joergPhoto}
                        name={t('footer.joergName')}
                        role={t('footer.joergRole')}
                        bio={t('footer.joergBio')}
                    />

                    {/* Vertical divider — desktop only */}
                    {!isTablet && (
                        <div style={{
                            width: '1px',
                            alignSelf: 'stretch',
                            background: 'rgba(255,255,255,0.08)',
                            flexShrink: 0,
                        }} />
                    )}

                    <FounderCard
                        photo={prasadPhoto}
                        name={t('footer.prasadName')}
                        role={t('footer.prasadRole')}
                        bio={t('footer.prasadBio')}
                    />
                </div>

                {/* ── Mission quote ── */}
                <div style={{
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    paddingTop: '2rem',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <p style={{
                        color: '#D1D5DB',
                        fontSize: '1rem',
                        lineHeight: '1.8',
                        maxWidth: '560px',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        marginBottom: '0.6rem',
                    }}>
                        {t('footer.body')}
                    </p>
                    <p style={{
                        color: '#9CA3AF',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        textAlign: 'center',
                        marginBottom: '2rem',
                    }}>
                        {t('footer.author')}
                    </p>

                    {/* ── Links + copyright ── */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1rem',
                    }}>
                        <a
                            href="https://megabitsystems37-my.sharepoint.com/:w:/g/personal/prasad_retterplus_com/IQDpzJivhzsDSIHEiPgo1M9ZAcdZy2kT3SE-A3_nljezpB4"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#9CA3AF', padding: '0 1rem', textDecoration: 'none', fontSize: '0.85rem' }}
                        >
                            {t('footer.privacyPolicy')}
                        </a>
                        <div style={{ width: '1px', height: '12px', background: 'rgba(255,255,255,0.2)' }} />
                        <a
                            href="mailto:prasad@retterplus.com"
                            style={{ color: '#9CA3AF', padding: '0 1rem', textDecoration: 'none', fontSize: '0.85rem' }}
                        >
                            {t('footer.contact')}
                        </a>
                    </div>

                    <p style={{ fontSize: '0.8rem', color: '#4B5563' }}>
                        {'© ' + t('footer.copyright').replace('{year}', new Date().getFullYear())}
                    </p>
                </div>

            </div>
        </footer>
    );
};

export default Footer;
